import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import RegisterUserService from "../../services/RegisterUserService";
import $ from "jquery";
import "datatables.net";
import { MdAddCircleOutline } from "react-icons/md";
import { FaUsers, FaPhoneAlt, FaMapMarkerAlt } from "react-icons/fa";
import AuthService from "../../services/AuthService";

const AVATAR_PALETTE = [
  "linear-gradient(135deg,#6366f1,#4f46e5)",
  "linear-gradient(135deg,#06b6d4,#0891b2)",
  "linear-gradient(135deg,#22c55e,#15803d)",
  "linear-gradient(135deg,#f59e0b,#d97706)",
  "linear-gradient(135deg,#ec4899,#be185d)",
  "linear-gradient(135deg,#8b5cf6,#6d28d9)",
];

const getInitials = (first = "", last = "") =>
  `${(first[0] || "").toUpperCase()}${(last[0] || "").toUpperCase()}` || "U";

const getAvatarColor = (seed = "") => {
  let hash = 0;
  for (let i = 0; i < seed.length; i++)
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0;
  return AVATAR_PALETTE[hash % AVATAR_PALETTE.length];
};

const getRoleClass = (name = "") => {
  const key = name.toLowerCase();
  if (key === "admin") return "role-admin";
  if (key === "manager") return "role-manager";
  if (key === "employee") return "role-employee";
  if (key === "superadmin") return "role-superadmin";
  return "role-default";
};

function RegisterUser() {
  const [User, setUser] = useState([]);
  const toast = useRef(null);
  const tableRef = useRef(null);
  const clientId = AuthService.getLoggedInClientId();

  const navigate = useNavigate();

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const users = await RegisterUserService.getUsersByClientId(clientId);
        setUser(users);
      } catch (error) {
        console.error("Error fetching Users: ", error);
        toast.current.show({
          severity: "error",
          summary: "Error",
          detail: "Failed to fetch users data",
        });
      }
    };
    fetchUsers();
    setTimeout(() => {
      $("#tableData").DataTable();
    }, 1000);
  }, [clientId]);

  /** Legacy role id 3 (removed tenant “Manager”) still appears in historical rows — display as Admin. */
  function displayTenantRole(roleId) {
    const r = Number(roleId);
    if (r === 3) return "Admin";
    if (r === 1) return "SuperAdmin";
    if (r === 2) return "Admin";
    if (r === 4) return "Employee";
    return "Unknown";
  }

  const visibleUsers = User.filter((user) => Number(user.roleId) !== 1);

  function renderUsers() {
    return visibleUsers.map((user) => {
      const roleName = displayTenantRole(user.roleId);
      const seed = `${user.firstName || ""}${user.lastName || ""}${user.email || ""}`;
      const address = [
        user.streetAddress,
        user.city,
        user.state,
        user.country,
        user.postalCode,
      ]
        .filter(Boolean)
        .join(", ");

      return (
        <tr key={user.id}>
          <td className="align-middle">
            <div className="user-cell">
              <span
                className="user-avatar"
                style={{ background: getAvatarColor(seed) }}
              >
                {getInitials(user.firstName, user.lastName)}
              </span>
              <span className="user-cell-meta">
                <span className="user-cell-name">
                  {user.firstName} {user.lastName}
                </span>
                <span className="user-cell-sub">{user.email}</span>
              </span>
            </div>
          </td>
          <td className="align-middle">
            <a
              href={`mailto:${user.email}`}
              className="cell-strong"
              style={{ textDecoration: "none" }}
            >
              {user.email}
            </a>
          </td>
          <td className="align-middle">
            <span className="cell-muted d-inline-flex align-items-center gap-2">
              <FaPhoneAlt style={{ color: "#94a3b8", fontSize: "0.75rem" }} />
              {user.contactNumber || "—"}
            </span>
          </td>
          <td className="align-middle">
            <span
              className="cell-muted d-inline-flex align-items-start gap-2"
              style={{ maxWidth: 280 }}
            >
              <FaMapMarkerAlt
                style={{ color: "#94a3b8", fontSize: "0.8rem", marginTop: 3 }}
              />
              <span>{address || "—"}</span>
            </span>
          </td>
          <td className="align-middle">
            <span className={`role-badge ${getRoleClass(roleName)}`}>
              {roleName}
            </span>
          </td>
          <td className="align-middle text-center">
            <span
              className={`status-pill ${user.isActive ? "is-active" : "is-inactive"}`}
            >
              {user.isActive ? "Active" : "Inactive"}
            </span>
          </td>
          <td className="align-middle text-center">
            <button
              className="action-btn"
              title="Edit"
              onClick={() => navigate(`/edituser/${user.id}`)}
            >
              <i className="pi pi-pencil" />
            </button>
            <button
              className="action-btn danger"
              title="Delete"
              onClick={() => deleteUser(user.id)}
            >
              <i className="pi pi-trash" />
            </button>
          </td>
        </tr>
      );
    });
  }

  const deleteUser = async (userId) => {
    try {
      await RegisterUserService.deleteUser(userId);
      toast.current.show({
        severity: "success",
        summary: "Success",
        detail: "User deleted successfully",
        life: 3000,
      });
      setUser(User.filter((user) => user.id !== userId));
    } catch (e) {
      toast.current.show({
        severity: "error",
        summary: "Error",
        detail: "Failed to delete user",
        life: 3000,
      });
      console.log(e);
    }
  };

  return (
    <div>
      <Toast ref={toast} />
      <div className="col-12">
        <div className="list-card my-4">
          <div className="list-card-header">
            <div className="list-card-title">
              <span className="list-card-icon">
                <FaUsers />
              </span>
              <div>
                <h5>Team Members</h5>
                <p>Manage your registered users and their roles</p>
              </div>
            </div>
            <div className="list-card-actions">
              <span className="list-count-pill">
                {visibleUsers.length}{" "}
                {visibleUsers.length === 1 ? "user" : "users"}
              </span>
              <button
                type="button"
                className="btn btn-primary mb-0 d-inline-flex align-items-center gap-2"
                onClick={() => navigate("/adduser")}
              >
                <MdAddCircleOutline style={{ fontSize: "1.1rem" }} />
                Add Employee
              </button>
            </div>
          </div>
          <div className="card-body px-0 pb-2 pt-0">
            <div className="table-responsive p-0">
              <table className="table align-items-center mb-0" id="tableData">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Email</th>
                    <th>Contact</th>
                    <th>Address</th>
                    <th>Role</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>{renderUsers()}</tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default RegisterUser;
